import { useTranslation } from 'react-i18next';
import { Globe, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ApiCallError } from '@/lib/api';
import {
  useGrantPatientPortalAccess,
  usePatientPortalInvitation,
  useRevokePatientPortalAccess,
} from '@/hooks/usePatientPortal';
import type { PatientRow } from '@/hooks/usePatients';
import { Button } from '@/components/ui/Button';

interface PatientPortalSectionProps {
  patient: PatientRow;
  canManage: boolean;
  variant?: 'card' | 'inline' | 'details';
}

export function PatientPortalSection({
  patient,
  canManage,
  variant = 'card',
}: PatientPortalSectionProps) {
  const { t } = useTranslation();
  const grant = useGrantPatientPortalAccess(patient.id);
  const revoke = useRevokePatientPortalAccess(patient.id);
  const { data: pendingInvite, isLoading: inviteLoading } = usePatientPortalInvitation(patient.id);

  const hasPortalAccess = Boolean(patient.portal_user_id);
  const hasEmail = Boolean(patient.email?.trim());

  const onGrant = async () => {
    try {
      const result = await grant.mutateAsync();
      if (result.status === 'linked') {
        toast.success(t('patients.portal.toast_linked', { email: result.email }));
      } else {
        toast.success(t('patients.portal.toast_invited', { email: result.email }));
      }
    } catch (err) {
      if (err instanceof ApiCallError) {
        if (err.code === 'conflict') {
          toast.error(t('patients.portal.error_already_has_access'));
        } else if (err.code === 'invalid-input') {
          toast.error(err.message);
        } else {
          toast.error(t('patients.portal.error_grant_failed'));
        }
      } else {
        toast.error(t('patients.portal.error_grant_failed'));
      }
    }
  };

  const onRevoke = async () => {
    if (!window.confirm(t('patients.portal.confirm_revoke'))) return;

    try {
      await revoke.mutateAsync();
      toast.success(t('patients.portal.toast_revoked'));
    } catch (err) {
      if (err instanceof ApiCallError) {
        toast.error(err.message || t('patients.portal.error_revoke_failed'));
      } else {
        toast.error(t('patients.portal.error_revoke_failed'));
      }
    }
  };

  const statusContent = inviteLoading ? (
    <Loader2 className="h-3.5 w-3.5 animate-spin text-ds-muted" />
  ) : hasPortalAccess ? (
    <span className="inline-flex items-center gap-1 text-ds-green-ink">
      {variant === 'details' ? null : <ShieldCheck className="h-3.5 w-3.5" />}
      {variant === 'details'
        ? t('patients.header.portal_active_value')
        : t('patients.portal.status_active')}
    </span>
  ) : pendingInvite ? (
    <span className="text-ds-amber-ink">{t('patients.portal.status_pending')}</span>
  ) : !hasEmail ? (
    <span className="text-ds-amber-ink">{t('patients.portal.needs_email')}</span>
  ) : (
    <span className="text-ds-muted">{t('patients.portal.not_enabled')}</span>
  );

  const actionButton =
    canManage && hasPortalAccess ? (
      <Button
        type="button"
        variant="link"
        className={variant === 'details' ? 'h-auto min-h-10 px-0' : 'ml-2'}
        onClick={onRevoke}
        disabled={revoke.isPending}
      >
        {revoke.isPending ? '…' : t('patients.portal.revoke')}
      </Button>
    ) : canManage && !hasPortalAccess && hasEmail && !pendingInvite ? (
      <Button
        type="button"
        variant="link"
        className={variant === 'details' ? 'h-auto min-h-10 px-0' : 'ml-2'}
        onClick={onGrant}
        disabled={grant.isPending}
      >
        {grant.isPending ? t('patients.portal.granting') : t('patients.portal.grant')}
      </Button>
    ) : null;

  if (variant === 'details') {
    return (
      <span className="inline-flex min-w-0 flex-wrap items-center justify-end gap-s2 nav:justify-start">
        {statusContent}
        {actionButton}
      </span>
    );
  }

  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-1">
        <Globe className="h-3.5 w-3.5 text-ds-muted" />
        <span className="font-medium text-ds-muted">{t('patients.portal.title')}:</span>
        {statusContent}
        {actionButton}
      </span>
    );
  }

  return (
    <div className="rounded-lg border border-ds-line bg-ds-card p-5 shadow-card">
      <div className="mb-3 flex items-center gap-2">
        <Globe className="h-4 w-4 text-ds-teal" />
        <h3 className="text-sm font-semibold text-ds-text">
          {t('patients.portal.title')}
        </h3>
      </div>

      <p className="mb-3 text-xs text-ds-muted">
        {t('patients.portal.description')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {statusContent}
        {actionButton}
      </div>
    </div>
  );
}
